<template>
  <div class="container mx-auto max-w-screen-lg px-4">
    <div class="mt-10 md:mt-32">
      <h1 class="landing_h1 mb-4 text-center md:mb-16 md:text-5xl">
        <Translate translate-key="cardholder_commissions.title" />
      </h1>
      <div class="prose prose-gray max-w-none">
        <Translate translate-key="cardholder_commissions.body" />
      </div>
      <NuxtImg
        v-if="file"
        :src="file.url"
        :width="scaledWidth"
        :height="scaledHeight"
        format="webp"
        sizes="sm:760px md:50vw lg:1024px"
        fetchpriority="high"
        :alt="t('page_data.commissions-for-selling-tradelines.seo_title')"
        class="mt-6"
      />
      <div
        class="mb-10 mt-10 flex flex-col items-center justify-center gap-4 md:mb-32 md:mt-20 md:flex-row md:gap-12"
      >
        <Button
          v-if="!currentUserStore.isAuthenticated"
          id="signupCardholder"
          as="a"
          :href="linkHref('/signup?type=cardholder')"
          variant="landing1"
          size="xl"
        >
          <Translate translate-key="signup_form.title" />
        </Button>
        <Button
          id="learnMore"
          as="a"
          :href="linkHref('/selling-tradelines')"
          variant="landingOutline"
          size="xl"
        >
          <Translate translate-key="cardholder_commissions.learn_more" />
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: "public" });

import Translate from "~/shared/components/i18n/Translate/Translate.vue";
import { Button } from "~/shared/components/ui/button";
import { useAuthFetch } from "~/shared/composables/useAuthFetch";
import { useCurrentUserStore } from "~/stores/userStore";

const LATEST_COMMISSIONS_KEY = "latest_commissions";
const MAX_WIDTH = 1024;

type ConfigFile = { url: string; width: number; height: number };
type ConfigFilesResponse = Record<string, ConfigFile>;

const { t } = useI18n();
const config = useRuntimeConfig();
const { authFetch } = useAuthFetch();
const currentUserStore = useCurrentUserStore();
const { linkHref } = useNativeRouteLink();

const { data: file, error } = await useAsyncData(
  "commissions-for-selling-tradelines-file",
  async () => {
    const data = await authFetch<ConfigFilesResponse>(
      `${config.public.baseURL}v1/configurations/files/`,
    );
    if (!data || !(LATEST_COMMISSIONS_KEY in data)) {
      throw createError({
        statusCode: 404,
        statusMessage: "Instructions not found.",
      });
    }
    return data[LATEST_COMMISSIONS_KEY] as ConfigFile;
  },
);

const scale = computed(() => {
  if (!file.value) return 1;
  return file.value.width > MAX_WIDTH ? MAX_WIDTH / file.value.width : 1;
});

const scaledWidth = computed(() =>
  file.value ? Math.round(file.value.width * scale.value) : undefined,
);
const scaledHeight = computed(() =>
  file.value ? Math.round(file.value.height * scale.value) : undefined,
);

const route = useRoute();
const siteUrl = (config.public.siteUrl as string) || "";

useSeoMeta({
  title: () => t("page_data.commissions-for-selling-tradelines.seo_title"),
  description: () => t("page_data.commissions-for-selling-tradelines.seo_description"),
  ogTitle: () => t("page_data.commissions-for-selling-tradelines.seo_title"),
  ogDescription: () => t("page_data.commissions-for-selling-tradelines.seo_description"),
  ogUrl: () => (siteUrl ? `${siteUrl}${route.path}` : undefined),
  ogImage: () => (siteUrl ? `${siteUrl}/home/gfs-group-tradelines-for-sale-800w.webp` : undefined),
  ogImageAlt: () => t("page_data.commissions-for-selling-tradelines.seo_title"),
  titleTemplate: () => "%s | GFS Group Tradelines",
});
useHead(() => ({
  meta: [{ name: "keywords", content: t("page_data.commissions-for-selling-tradelines.seo_keywords") }],
}));
</script>

